import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, ChevronLeft, User, Search, X } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useAuth } from "../../context/AuthContext";
import { GlobalSearch } from "../common/GlobalSearch";

const pageNames = {
  "/dashboard": "Dashboard",
  "/leads": "Leads",
  "/customers": "Customers",
  "/bookings": "Bookings",
  "/complaints": "Complaints",
  "/due": "Due Customers",
  "/overdue": "Overdue Customers",
  "/delayed": "Delayed Customers",
  "/no-show": "No Show Customers",
  "/no-show-campaign": "No Show Campaign",
  "/follow-ups": "Follow-ups",
  "/branches": "Branches",
  "/cre-management": "CRE Management",
  "/settings/api-integrations": "API Integrations",
  "/reports": "Reports",
  "/search": "Search Results",
  "/calls-done": "Calls Done",
  "/data-import": "Data Import"
};

export const MobileAppBar = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showSearch, setShowSearch] = useState(false);
  
  const currentPage = pageNames[location.pathname] || "Servall CRM";
  const isDetailPage = location.pathname.includes("/customers/") || location.pathname.includes("/leads/");

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Search mode - full width search bar
  if (showSearch) {
    return (
      <header className="mobile-app-bar md:hidden" data-testid="mobile-app-bar">
        <div className="flex-1 relative">
          <GlobalSearch isMobile={true} className="w-full" />
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="w-9 h-9 ml-2 flex-shrink-0"
          onClick={() => setShowSearch(false)}
          data-testid="mobile-search-close"
        >
          <X className="w-5 h-5" />
        </Button>
      </header>
    );
  }

  return (
    <header className="mobile-app-bar md:hidden" data-testid="mobile-app-bar">
      {/* Left: Menu or Back button */}
      <div className="flex items-center gap-2">
        {isDetailPage ? (
          <Button
            variant="ghost"
            size="icon"
            className="w-9 h-9"
            onClick={handleBack}
            data-testid="mobile-back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="w-9 h-9"
            onClick={onMenuClick}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Center: Page Title */}
      <div className="flex-1 text-center">
        <h1 className="font-heading font-semibold text-sm truncate px-2">
          {currentPage}
        </h1>
      </div>

      {/* Right: Search & Profile */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="w-9 h-9"
          onClick={() => setShowSearch(true)}
          data-testid="mobile-search-btn"
        >
          <Search className="w-5 h-5" />
        </Button>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="w-9 h-9"
              data-testid="mobile-profile-btn"
            >
              <div className="w-7 h-7 rounded-full bg-[#ED1C24] text-white flex items-center justify-center text-xs font-semibold">
                {user?.name?.charAt(0).toUpperCase() || "U"}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-3 py-2">
              <p className="font-medium text-sm">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default MobileAppBar;
